import { Calendar, User2, Paperclip, Timer, Check, Undo2, MessageSquare, Repeat, CornerDownRight, ListChecks } from 'lucide-react';
import { Badge } from './ui/badge';
import { TYPE_MAP, PRIORITY_MAP, RECURRENCE_MAP, isRecurring } from '../lib/constants';
import { formatDueDate, dueState } from '../lib/dates';
import { cn } from '../lib/utils';
import UserAvatar from './UserAvatar';

const DUE_CHIP = {
  overdue: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  today: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  soon: 'bg-muted text-muted-foreground',
  future: 'bg-muted text-muted-foreground',
};

// Row actions (take it / done / reopen). `hover` = only revealed on hover on
// desktop (compact cards); always visible on touch layouts where there is no hover.
function Actions({ ticket, onTake, onComplete, onReopen, myId, hover }) {
  const isDone = ticket.status === 'done';
  const take = onTake && !ticket.assignees?.some((a) => a.id === myId);
  const complete = ticket.status === 'validation' && onComplete;
  const reopen = isDone && onReopen;
  if (!take && !complete && !reopen) return null;
  return (
    <span className={cn('flex shrink-0 items-center gap-1', hover && 'md:hidden md:group-hover:flex md:group-focus-within:flex')}>
      {take ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTake(); }}
          className="rounded px-1.5 py-0.5 text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
          title="Add me as an assignee"
        >
          + take it
        </button>
      ) : null}
      {complete ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onComplete(); }}
          className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-muted-foreground transition hover:bg-emerald-500/10 hover:text-emerald-600 dark:hover:text-emerald-400"
          title="Approve & move to done"
        >
          <Check className="h-3 w-3" /> done
        </button>
      ) : null}
      {reopen ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onReopen(); }}
          className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-muted-foreground transition hover:bg-accent hover:text-foreground"
          title="Reopen (move back to to-do)"
        >
          <Undo2 className="h-3 w-3" /> reopen
        </button>
      ) : null}
    </span>
  );
}

// "2/5" subtasks-done chip; green once everything is ticked.
function SubtaskChip({ stats, className }) {
  if (!stats?.total) return null;
  const all = stats.done === stats.total;
  return (
    <span className={cn('flex items-center gap-0.5', all && 'text-emerald-600 dark:text-emerald-400', className)} title={`${stats.done} of ${stats.total} subtasks done`}>
      <ListChecks className="h-3 w-3" />{stats.done}/{stats.total}
    </span>
  );
}

// "↳ #12" for a subtask, linking it back to its parent in the tooltip.
function ParentRef({ parent, className }) {
  if (!parent) return null;
  return (
    <span className={cn('flex min-w-0 items-center gap-0.5 truncate', className)} title={`subtask of #${parent.id} ${parent.title}`}>
      <CornerDownRight className="h-3 w-3 shrink-0" />#{parent.id}
    </span>
  );
}

function Avatars({ assignees }) {
  if (!assignees?.length) {
    return <span className="h-4 w-4 shrink-0 rounded-full border border-dashed border-muted-foreground/40" title="unassigned" />;
  }
  return (
    <span className="flex shrink-0 -space-x-1" title={assignees.map((a) => a.name).join(', ')}>
      {assignees.slice(0, 3).map((a) => (
        <UserAvatar key={a.id} user={a} className="h-4 w-4 rounded-sm ring-1 ring-card" />
      ))}
      {assignees.length > 3 ? <span className="pl-1.5 text-[10px] text-muted-foreground">+{assignees.length - 3}</span> : null}
    </span>
  );
}

// A draggable board card. Drag wiring is passed in from the board so the card
// itself stays presentational. `compact` collapses it to three tight rows
// (id/type, title, one footer) so busy columns stay scannable.
export default function TicketCard({ ticket, onClick, onTake, onComplete, onReopen, myId, onDragStart, onDragEnd, onDrop, onDragOver, dragging, compact = false, subtasks }) {
  const type = TYPE_MAP[ticket.type];
  const priority = PRIORITY_MAP[ticket.priority];
  const isDone = ticket.status === 'done';
  const state = isDone ? 'future' : dueState(ticket.due_date);
  const actions = { ticket, onTake, onComplete, onReopen, myId };

  if (compact) {
    return (
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onClick={onClick}
        className={cn(
          'group cursor-pointer rounded-md border border-border bg-card px-2.5 py-2 shadow-xs transition hover:border-primary/40 hover:shadow-sm',
          dragging && 'opacity-40',
        )}
      >
        <div className="flex items-center gap-1.5 text-[11px] leading-4 text-muted-foreground">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', priority?.dot)} title={`priority: ${priority?.label}`} />
          <span>#{ticket.id}</span>
          <span className={cn('rounded px-1 text-[10px] font-medium', type?.badge)}>{type?.key}</span>
          {isRecurring(ticket) ? <Repeat className="h-3 w-3" title={`repeats: ${RECURRENCE_MAP[ticket.recurrence]?.label}`} /> : null}
          <ParentRef parent={ticket.parent} />
          <span className="flex-1" />
          <Actions {...actions} hover />
        </div>
        <p className={cn('clamp-2 mt-1 text-[13px] font-medium leading-snug', isDone && 'text-muted-foreground line-through')}>
          {ticket.title}
        </p>
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Avatars assignees={ticket.assignees} />
          {ticket.requestor ? (
            <span className="flex min-w-0 flex-1 items-center gap-1" title={`requested by ${ticket.requestor.name}`}>
              {ticket.assignees?.length ? <span className="text-muted-foreground/50">·</span> : null}
              <User2 className="h-3 w-3 shrink-0" />
              <span className="truncate">{ticket.requestor.name}</span>
            </span>
          ) : <span className="flex-1" />}
          <span className="flex shrink-0 items-center gap-1.5">
            <SubtaskChip stats={subtasks} />
            {ticket.estimate_hours != null ? <span title={`estimate: ${ticket.estimate_hours}h`}>{ticket.estimate_hours}h</span> : null}
            {ticket.attachments?.length ? <Paperclip className="h-3 w-3" title={`${ticket.attachments.length} screenshot(s)`} /> : null}
            {ticket.comment_count > 0 ? (
              <span className="flex items-center gap-0.5" title={`${ticket.comment_count} message(s)`}>
                <MessageSquare className="h-3 w-3" />{ticket.comment_count}
              </span>
            ) : null}
            {ticket.due_date ? (
              <span className={cn('flex items-center gap-1 rounded px-1 py-px', DUE_CHIP[state])} title={`due ${formatDueDate(ticket.due_date)}`}>
                <Calendar className="h-3 w-3" />
                {formatDueDate(ticket.due_date, { short: true })}
              </span>
            ) : null}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onClick={onClick}
      className={cn(
        'group cursor-pointer rounded-lg border border-border bg-card p-3 shadow-xs transition hover:border-primary/40 hover:shadow-sm',
        dragging && 'opacity-40',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">#{ticket.id}</span>
          <Badge className={type?.badge}>{type?.key}</Badge>
          <ParentRef parent={ticket.parent} className="text-[11px] text-muted-foreground" />
        </div>
        <span
          className={cn('mt-1 h-2.5 w-2.5 shrink-0 rounded-full', priority?.dot)}
          title={`priority: ${priority?.label}`}
        />
      </div>

      <p className={cn('mt-2 text-sm font-medium leading-snug', isDone && 'text-muted-foreground line-through')}>
        {ticket.title}
      </p>

      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1" title={ticket.requestor?.name || 'No requestor'}>
          <User2 className="h-3 w-3 shrink-0" />
          <span className="truncate">{ticket.requestor?.name || 'No requestor'}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <SubtaskChip stats={subtasks} />
          {ticket.estimate_hours != null ? (
            <span className="flex items-center gap-0.5" title={`estimate: ${ticket.estimate_hours}h`}>
              <Timer className="h-3 w-3" />{ticket.estimate_hours}h
            </span>
          ) : null}
          {ticket.attachments?.length ? (
            <span className="flex items-center gap-0.5" title={`${ticket.attachments.length} screenshot(s)`}>
              <Paperclip className="h-3 w-3" />{ticket.attachments.length}
            </span>
          ) : null}
          {ticket.comment_count > 0 ? (
            <span className="flex items-center gap-0.5" title={`${ticket.comment_count} message(s)`}>
              <MessageSquare className="h-3 w-3" />{ticket.comment_count}
            </span>
          ) : null}
          {isRecurring(ticket) ? (
            <span className="flex items-center" title={`repeats: ${RECURRENCE_MAP[ticket.recurrence]?.label}`}>
              <Repeat className="h-3 w-3" />
            </span>
          ) : null}
          {ticket.due_date ? (
            <span className={cn('flex items-center gap-1 rounded px-1.5 py-0.5', DUE_CHIP[state])}>
              <Calendar className="h-3 w-3" />
              {formatDueDate(ticket.due_date)}
            </span>
          ) : null}
        </span>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 border-t border-border/60 pt-2 text-xs">
        <span className="min-w-0 flex-1">
          {ticket.assignees?.length ? (
            <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground" title={ticket.assignees.map((a) => a.name).join(', ')}>
              <span className="flex -space-x-1">
                {ticket.assignees.slice(0, 3).map((a) => (
                  <UserAvatar key={a.id} user={a} className="h-4 w-4 rounded-sm ring-1 ring-card" />
                ))}
              </span>
              <span className="truncate">
                {ticket.assignees.length === 1 ? `@${ticket.assignees[0].username}` : `${ticket.assignees.length} assignees`}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground/50">unassigned</span>
          )}
        </span>
        <Actions {...actions} />
      </div>
    </div>
  );
}
